import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "../../../components/ui/Calendar";
import { el } from 'date-fns/locale';
import { Button } from "../../../components/ui/Button";
import { toast } from "../../../components/ui/Toast/useToast";
import { size } from "lodash";
import Loader from "../../../components/elements/loader";
import { undoUntil, validateResponse } from "../deliveryUtils";
import clsx from "clsx";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useDeliveryFlowStore } from "../../../store/newVehicleDeliveryStore";
import { useDeliveryAddress } from "../../../hooks/react-query/useVehicleDelivery";
import { DELIVERY_BOOKING_STEPS } from "../../../config/constants";
import { AlertBanner } from '../../../components/ui/AlertBanner';
import { CheckCircleIcon } from "../../../icons";
import { ToggleButton } from "../../../components/ui/Toggle/ToggleButton";
import { TextField } from "../../../components/ui";
import { TimeSlotSection } from "./TimeSlotSection";

export const BookingStepTwo = () => {

  // GLOBAL STATE:
  const bookingCalendarOptions = useDeliveryFlowStore((s) => s.bookingCalendarOptions);
  const deliveryAddress = useDeliveryFlowStore((s) => s.deliveryAddress);
  const flowId = useDeliveryFlowStore((s) => s.flowId);
  const flowStep = useDeliveryFlowStore((s) => s.flowStep);
  const isVehicleDeliverySelected = useDeliveryFlowStore((s) => s.isVehicleDeliverySelected);
  const selectedCalendarOption = useDeliveryFlowStore((s) => s.selectedCalendarOption);
  const interimVehiclePlate = useDeliveryFlowStore((s) => s.interimVehiclePlate);

  const setDeliveryAddress = useDeliveryFlowStore((s) => s.setDeliveryAddress);
  const setSelectedCalendarOption = useDeliveryFlowStore((s) => s.setSelectedCalendarOption);
  const setBookingCalendarOptions = useDeliveryFlowStore((s) => s.setBookingCalendarOptions);
  const setFlowStep = useDeliveryFlowStore((s) => s.setFlowStep);
  const setInterimVehiclePlate = useDeliveryFlowStore((s) => s.setInterimVehiclePlate);

  
  // LOCAL STATE: 
  const [addressSearchButtonDisabled, setAddressSearchButtonDisabled] = useState(true)
  const [isUndoLoading, setIsUndoLoading] = useState(false)
  const [futureDate, setFutureDate] = useState(new Date())
  const today = useMemo(() => new Date(), []);
  const [showMap, setShowMap] = useState(false)
  const [center, setCenter] = useState({
    lat: 37.9838,
    lng: 23.7275, // Athens
  });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [formattedAddress, setFormattedAddress] = useState("")
  const targetRef = useRef(null);
  const calendarRef = useRef(null);
  const [calendarHeight, setCalendarHeight] = useState(null);
  const [interimVehicleToggle, setInterimVehicleToggle] = useState(false)
  
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDXFLTkJcZ-Pyn5uI0KLzUoyPZ8Ay1dRJk",
    libraries: ["places"],
    language: "el",
    region: "GR",
  });

  const containerStyle = {
    margin: '32px 0 0 0',
    width: '100%',
    height: '400px',
    borderRadius: '12px',
  };

  const mapRef = useRef(null);

  // FETCH DATA:

  // LOCAL VARIABLES:
  const {mutateAsync: selectDeliveryAddress, isLoading: isDeliveryAddressLoading} = useDeliveryAddress()
  
  // USE EFFECTS:
  
  useEffect(()=>{
    if(
      deliveryAddress?.street && 
      deliveryAddress?.number && 
      deliveryAddress?.area && 
      deliveryAddress?.zipCode
    ) {
      setAddressSearchButtonDisabled(false)
    }
    else {
      setAddressSearchButtonDisabled(true)
    }
  },[deliveryAddress])

  useEffect(() => {
    if (!bookingCalendarOptions?.length) return;

    const last = [...bookingCalendarOptions]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .at(-1);

    if (last?.startTime) {
      setFutureDate(new Date(last.startTime));
    }
  }, [bookingCalendarOptions]);

  
  useEffect(() => {
    if (!calendarRef.current) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      setCalendarHeight(entry.contentRect.height);
    });

    observer.observe(calendarRef.current);

    return () => observer.disconnect();
  }, []);

  // HANDLERS & FUNCTIONS

  const getSlotsForDay = (slots, selectedDate) => {
    if (!selectedDate) return [];

    return slots?.filter(slot => {
      const slotDate = new Date(slot.startTime);
      return slotDate.toDateString() === selectedDate.toDateString();
    });
  };

  const availableDays = useMemo(() => {
    if (!Array.isArray(bookingCalendarOptions)) return [];

    return bookingCalendarOptions
      .filter(s => s?.startTime)
      .map(s => {
        const d = new Date(s.startTime);
        return isNaN(d) ? null : d.toDateString();
      })
      .filter(Boolean);
  }, [bookingCalendarOptions]);

  const daySlots = useMemo(() => {
    return getSlotsForDay(
      bookingCalendarOptions,
      selectedCalendarOption.selectedDate
    );
  }, [bookingCalendarOptions, selectedCalendarOption.selectedDate]);

    const searchPickupAddress = async () => {
    // const inputAddress = `${state?.pickupCRMFormAddress?.street} ${state?.pickupCRMFormAddress?.number}, ${state?.pickupCRMFormAddress?.area} ${state?.pickupCRMFormAddress?.zipCode} Ελλάδα`
    const payload = { deliveryAddress: formattedAddress}
    try{
        const response = await selectDeliveryAddress({vehicleFlowId:flowId, payload})

        const isResponseValid = validateResponse(response)

        if (isResponseValid) {
          const bookingCalendarOptions = response?.inputs?.bookingCalendar?.options?.map((option) => {return {startTime: option?.StartTime, endTime:option?.EndTime}})

          const fdate = new Date(bookingCalendarOptions[bookingCalendarOptions?.length - 1]?.startTime)
          setFutureDate(fdate);
          // set New State based on the response
          setBookingCalendarOptions(bookingCalendarOptions)
          setFlowStep(response?.name)
          setSelectedCalendarOption({selectedDate:null, selectedSlots:[]})
          
          setShowMap(false)
          setMarkerPosition(null)
          if (targetRef.current) {
            targetRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end"
            });
          }
        } else {
          const message = response?.exceptions?.deliveryAddress?.[0]
            toast({
              variant: 'destructive',
              description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
            });
        }
      } catch(e) {
        console.warn(e)
        toast({
          variant: 'destructive',
          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
        });
      }
  }

  const geocodeAddress = (address) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        
        const coords = {
          lat: location.lat(),
          lng: location.lng(),
        };

        setFormattedAddress(results?.[0]?.formatted_address)
        setCenter(coords);
        setMarkerPosition(coords);
        // mapRef.current.panTo(coords);
        // mapRef.current.setZoom(15);
      } else {
        console.error("Geocode failed:", status);
      }
    });
  };

  return (
    <div className='flex flex-col gap-6 pb-8 h-full w-full'>
      {(isDeliveryAddressLoading || isUndoLoading) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-[2px]">
          <Loader />
        </div>
      )}
      {isVehicleDeliverySelected && <>
        <h3>1. Διεύθυνση παράδοσης</h3>
        <div className={`"shadow-md rounded-xl py-6 px-9 bg-white`}>
          <h4 className='pb-6'>Συμπλήρωσε τη διεύθυνση για τον προγραμματισμό του Home Delivery.</h4>
          <div className='grid grid-cols-9 gap-5'>
            <div className='grid col-span-6'>
              <div className='flex flex-col gap-3'>
                <div>
                  <p className='text-sm text-grenite pb-2'>Οδός</p>
                  <input
                    disabled={false}
                    className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                    name={`street-pickup`}
                    value={deliveryAddress?.street}
                    onChange={(e) =>
                      setDeliveryAddress({...deliveryAddress, street:e?.target?.value})
                    }
                    />
                </div>
                <div>
                  <p className='text-sm text-grenite pb-2'>Περιοχή</p>
                  <input
                    disabled={false}
                    className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                    name={`street-area`}
                    value={deliveryAddress?.area}
                    onChange={(e) =>
                      setDeliveryAddress({...deliveryAddress, area:e?.target?.value})
                    }
                    />
                </div>
              </div>
            </div>
            <div className='grid col-span-3'>
              <div className='flex flex-col gap-3'>
                <div>
                  <p className='text-sm text-grenite pb-2'>Αριθμός</p>
                  <input
                    disabled={false}
                    className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                    name={`street-number`}
                    value={deliveryAddress?.number}
                    onChange={(e) =>
                      setDeliveryAddress({...deliveryAddress, number:e?.target?.value})
                    }
                    />
                  </div>
                  <div>
                  <p className='text-sm text-grenite pb-2'>Ταχ. κώδικας</p>
                  <input
                    disabled={false}
                    className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                    name={`street-zipCode`}
                    value={deliveryAddress?.zipCode}
                    onChange={(e) =>
                      setDeliveryAddress({...deliveryAddress, zipCode:e?.target?.value})
                    }
                    />
                  </div>
              </div>
            </div>
          </div>
          <div className='flex flex-row items-end justify-end pt-3'>
            <Button className={`${addressSearchButtonDisabled ? "text-fadedgrey" :"text-primary"} font-semibold` }
              disabled={addressSearchButtonDisabled}
              onClick={async ()=>{
                const inputAddress = `${deliveryAddress?.street} ${deliveryAddress?.number}, ${deliveryAddress?.area}, ${deliveryAddress?.zipCode}`
                geocodeAddress(inputAddress)
                setShowMap(true)
                }}>
              Εύρεση διεύθυνσης
            </Button>
          </div>
          {isLoaded ? showMap && <div>
            <h5 className='pt-5 pb-3'>Επιβεβαίωσε ότι η τοποθεσία στον χάρτη είναι σωστή. Αν όχι, τροποποίησε τα στοιχεία που συμπλήρωσες και αναζήτησε ξανά τη διεύθυνση.</h5>
            <h5 className='font-bold'>{formattedAddress}</h5>
            <GoogleMap
              key={showMap ? "map-open" : "map-closed"}
              mapContainerStyle={containerStyle}
              center={center}
              zoom={15}
              onLoad={(map) => (mapRef.current = map)}
              onUnmount={() => {
                mapRef.current = null
              }}
              options={{
                zoomControl: false, // Disable zoom control
                fullscreenControl: false, // Disable fullscreen control
                streetViewControl: false, // Disable street view control
                mapTypeControl: false, // Disable map type control
              }}
            >{markerPosition && <Marker position={markerPosition} />}</GoogleMap>
            <div className='flex flex-row items-end justify-end pt-3'>
              <Button className={`${addressSearchButtonDisabled ? "text-fadedgrey" :"text-primary"} font-semibold` }
                disabled={addressSearchButtonDisabled}
                onClick={async ()=>{
                  if(DELIVERY_BOOKING_STEPS[flowStep] > DELIVERY_BOOKING_STEPS["deliveryAddress"]){
                    setIsUndoLoading(true)
                    try{
                      // Adjust the undo logic to delivery booking
                      const undoStatus = await undoUntil({targetStep : "deliveryAddress"})
                      if(undoStatus?.success) {
                        searchPickupAddress()
                      } else {
                        toast({
                          variant: 'destructive',
                          description: undoStatus?.message,
                        });
                      }
                    } catch (e) {
                      console.warn(e)
                    } finally {setIsUndoLoading(false)}
                  }else {
                    searchPickupAddress()
                  }
                  }}>
                Επιβεβαίωση
              </Button>
            </div>
          </div> : <Loader />}
        </div>
      </>}
      {((isVehicleDeliverySelected && !showMap && formattedAddress) || (!isVehicleDeliverySelected)) && <>
        <h3>{`${isVehicleDeliverySelected ? "2. " :""}Επιλογή ημερομηνίας & ώρας`}</h3>
        <div className="bg-white shadow-md rounded-xl py-6 px-9 mt-3 flex flex-col gap-10">
          <div>
            <h4>Ποιο ραντεβού σε εξυπηρετεί;</h4>
            {availableDays?.length === 0 && <p className='text-primary text-xs pt-1'>Δυστυχώς δεν βρέθηκαν διαθέσιμα ραντεβού</p>}
          </div>
          <div className="grid grid-cols-7 items-start">
            <div className="col-span-5" ref={calendarRef}>
              <Calendar
                locale={el}
                fromDate={today}
                toDate={futureDate}
                mode="single"
                selected={selectedCalendarOption?.selectedDate}
                onSelect={(date) =>
                  setSelectedCalendarOption({...selectedCalendarOption, selectedDate:date})
                }
                disabled={(date) =>{
                  const key = date.toDateString();
                  return !availableDays.includes(key);
                }}
                modifiers={{
                  hasSlot: selectedCalendarOption?.selectedSlots?.map(
                    slot => new Date(slot.date + "T00:00:00")
                  )
                }}
                modifiersClassNames={{
                  hasSlot: "bg-[#D4002A]/15 rounded-full"
                }}
                className="lg:p-3 py-6 bg-transparent lg:shadow-none rounded-none border border-b-0 border-l-0 border-t-0 border-r-fadedgrey"
                classNames={{
                  row: 'flex w-full justify-between py-[11px]',
                  head_row: 'flex w-full justify-between py-[11px] mb-7',
                  nav_button_previous: 'absolute left-10',
                  nav_button_next: 'absolute right-10',
                  day: clsx(
                      'p-0 font-normal aria-selected:opacity-100 w-8 rounded-full h-8'
                    ),
                }}
              />
            </div>
            <div className="col-span-2 flex flex-col gap-6 pt-4 overflow-hidden">
              <h4 className="pl-7 shrink-0">Ώρα</h4>
              <div className="overflow-y-auto pr-2" style={{ maxHeight: calendarHeight-65 ?? "auto" }}>
                <TimeSlotSection
                  currentSlots={daySlots}
                  selectedSlots={selectedCalendarOption?.selectedSlots}
                />
              </div>
            </div>
          </div>
          <AlertBanner severity="info" className='pb-1'>
                <div className='flex flex-row gap-[14px] justify-start align-top p-3'>
                    <CheckCircleIcon width='20' height='20' className={'stroke-[#2E9BFF] shrink-0'} />
                    <p className='text-sm'>Η τιμολόγηση θα ξεκινήσει εντός 3 εργάσιμων ημερών από την ημερομηνία διαθεσιμότητας του αυτοκινήτου για παραλαβή.</p>
                </div>
            </AlertBanner>
            <div className={`flex flex-row gap-5 items-center ${!interimVehicleToggle && "py-[11px]"}`}>
              <ToggleButton
                  className='data-[state=checked]:bg-[#13EF00]'
                  checked={interimVehicleToggle} 
                  onCheckedChange={async (value)=>{
                    setInterimVehicleToggle(value)
                  }
                } />
                <p className='text-sm'>Θέλω να επιστρέψω αυτοκίνητο Avis</p>
                {interimVehicleToggle && <>
                  <p className='text-sm'>Πινακίδα αυτοκινήτου</p>
                  <TextField
                    autoComplete="off"
                    onChange={(e) => {
                      setInterimVehiclePlate(e.target.value.toUpperCase())
                    }}
                    min={0}
                    placeholder={'ΑΑΑ 1234'}
                    value={interimVehiclePlate}
                    className={" w-[115px]"}
                    />
                </>}
            </div>
        </div>
      </> } 
    </div>
  );
};
